import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const painPoints = [
  {
    icon: 'ri-building-4-line',
    title: 'Aggregators Eating Your Margin',
    body: 'Booking.com, Viator, and GetYourGuide are spending millions to outrank your own website — then charging 20–30% commission on every booking they send you. We build websites that compete directly, so travellers book with you instead.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Travellers Search on Mobile, Last Minute',
    body: "Your customer is standing at Mission Beach, looking up reef trips on their phone with 4G. If your site loads slow, looks wrong on mobile, or doesn't have a clear booking path, they're gone to whoever is next in the results.",
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Search Is Changing Who Gets Recommended',
    body: 'ChatGPT and Gemini now answer "best reef trips near Mission Beach" directly — recommending specific operators by name. If your business isn\'t in the AI\'s training data and citation network, you don\'t exist to that traveller.',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Seasons Kill Off-Peak Revenue',
    body: 'Tourism is seasonal — and most tourism websites have no strategy for converting shoulder-season traffic. We build content and SEO architecture that captures year-round intent, not just peak bookings.',
  },
  {
    icon: 'ri-image-2-line',
    title: 'Your Photography Deserves a Better Home',
    body: "Tourism is a visual sell. If your website isn't built to showcase your photography — full-bleed imagery, fast loading, responsive galleries — you're underselling the experience before anyone even reads a word.",
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Local Search Is a War Zone',
    body: "Every reef trip, charter, and experience operator in your region is fighting for the same Google results. We've won that war for our clients — and we know exactly what it takes to beat established operators and aggregators.",
  },
];

export default function TourismPainPointsSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="tourism-pain-points" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/40 to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-ds-red/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>
        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label">The Tourism Website Challenge</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-3">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              TOURISM WEBSITES
              <br />
              HAVE <span className="text-ds-red neon-text-red">UNIQUE</span>
              <br />
              PROBLEMS.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs">
              Generic web agencies don&apos;t understand seasonal demand, aggregator competition, or AI search. We do — because we live and work in it every day.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ds-border reveal">
          {painPoints.map((point, i) => (
            <div
              key={point.title}
              className="group bg-ds-bg p-8 hover:bg-ds-bg-2 transition-colors duration-300"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 mb-5">
                <i className={`${point.icon} text-ds-red text-lg`} />
              </div>
              <h3 className="font-display text-white text-sm tracking-wider leading-tight mb-3 group-hover:text-ds-red transition-colors duration-300">
                {point.title}
              </h3>
              <div className="h-px bg-ds-border mb-4 group-hover:bg-ds-red/20 transition-colors duration-300" />
              <p className="font-body text-white/50 text-sm leading-relaxed group-hover:text-white/65 transition-colors duration-300">
                {point.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
